import React, { useEffect, useState } from "react";
import {
  FiUsers,
  FiUserPlus,
  FiEye,
  FiActivity,
  FiMessageSquare,
  FiCopy,
  FiRefreshCw,
  FiLock,
} from "react-icons/fi";
import { useAuth } from "../../auth/AuthContext";
import { apiGet } from "../../auth/api";
import "./admin.css";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch (e) {
    return iso;
  }
};
const fmtDay = (d) => {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch (e) {
    return d;
  }
};

export default function Admin() {
  const { token, isAuthed } = useAuth();
  const [state, setState] = useState("loading"); // loading | ok | auth | forbidden | error
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = () => {
    setState("loading");
    apiGet("/admin", token)
      .then((d) => {
        setData(d);
        setState("ok");
      })
      .catch((err) => {
        if (err && err.status === 401) setState("auth");
        else if (err && err.status === 403) setState("forbidden");
        else setState("error");
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const copyEmails = () => {
    if (!data || !data.users) return;
    const list = data.users
      .map((u) => u.email)
      .filter(Boolean)
      .join(", ");
    const done = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(list).then(done, done);
    } else {
      const ta = document.createElement("textarea");
      ta.value = list;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch (e) {
        /* ignore */
      }
      document.body.removeChild(ta);
      done();
    }
  };

  const header = (
    <div className="page-header">
      <div className="page-emoji" role="img" aria-label="lock">
        🔐
      </div>
      <h1 className="page-title">Admin</h1>
      <p className="page-sub">Owner-only: visits, sign-ups, suggestions, and the member email list.</p>
    </div>
  );

  if (state === "loading") {
    return (
      <>
        {header}
        <div className="admin-msg">Loading…</div>
      </>
    );
  }

  if (state === "auth") {
    return (
      <>
        {header}
        <div className="admin-msg">
          <FiLock /> Please sign in with the owner account to view this page.
        </div>
      </>
    );
  }

  if (state === "forbidden") {
    return (
      <>
        {header}
        <div className="admin-msg">
          <FiLock /> This account isn’t the admin. {isAuthed ? "Sign in with the owner email." : ""}
        </div>
      </>
    );
  }

  if (state === "error" || !data) {
    return (
      <>
        {header}
        <div className="admin-msg">
          Couldn’t load admin data.{" "}
          <button className="nb-btn nb-btn-sm" onClick={load}>
            <FiRefreshCw size={13} /> Retry
          </button>
        </div>
      </>
    );
  }

  const t = data.totals || {};
  const series = data.series || [];
  const maxViews = series.reduce((m, s) => Math.max(m, s.views || 0), 0) || 1;
  const cards = [
    { icon: <FiUsers />, label: "Total users", value: t.users },
    { icon: <FiUserPlus />, label: "Sign-ups (7d)", value: t.signups7d },
    { icon: <FiActivity />, label: "Active (7d)", value: t.activeUsers7d },
    { icon: <FiEye />, label: "Page views (30d)", value: t.pageviews30d },
    { icon: <FiUsers />, label: "Unique visitors (30d)", value: t.uniqueVisitors30d },
    { icon: <FiMessageSquare />, label: "Suggestions", value: t.feedback },
  ];

  return (
    <>
      {header}

      <div className="admin-toolbar">
        <button className="nb-btn nb-btn-sm" onClick={load}>
          <FiRefreshCw size={13} /> Refresh
        </button>
      </div>

      <div className="admin-cards">
        {cards.map((c) => (
          <div className="admin-card" key={c.label}>
            <div className="admin-card-ico">{c.icon}</div>
            <div className="admin-card-val">{c.value != null ? c.value : "—"}</div>
            <div className="admin-card-lbl">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="admin-section">
        <h2 className="admin-h2">Visits — last 30 days</h2>
        {series.length === 0 ? (
          <div className="admin-empty">No page views recorded yet.</div>
        ) : (
          <div className="admin-bars">
            {series.map((s) => (
              <div className="admin-bar-col" key={s.day} title={`${s.day}: ${s.views} views, ${s.visitors} visitors`}>
                <div className="admin-bar" style={{ height: `${Math.max(4, (s.views / maxViews) * 100)}%` }} />
                <div className="admin-bar-x">{fmtDay(s.day)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="admin-section">
        <div className="admin-section-head">
          <h2 className="admin-h2">Members ({data.users.length})</h2>
          <button className="nb-btn nb-btn-sm nb-btn-primary" onClick={copyEmails}>
            <FiCopy size={13} /> {copied ? "Copied!" : "Copy all emails"}
          </button>
        </div>
        {data.users.length === 0 ? (
          <div className="admin-empty">No sign-ups yet.</div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Joined</th>
                  <th>Last login</th>
                  <th>Logins</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => (
                  <tr key={u.email}>
                    <td className="admin-email">{u.email}</td>
                    <td>{fmt(u.createdAt)}</td>
                    <td>{fmt(u.lastLogin)}</td>
                    <td>{u.loginCount || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="admin-section">
        <h2 className="admin-h2">Suggestions ({data.feedback.length})</h2>
        {data.feedback.length === 0 ? (
          <div className="admin-empty">No suggestions yet.</div>
        ) : (
          <ul className="admin-fb">
            {data.feedback.map((f) => (
              <li key={f.id} className="admin-fb-item">
                <div className="admin-fb-msg">{f.message}</div>
                <div className="admin-fb-meta">
                  <span>{f.email || "anonymous"}</span>
                  <span>·</span>
                  <span>{f.path || "—"}</span>
                  <span>·</span>
                  <span>{fmt(f.createdAt)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
